import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, MapPin, Trophy, ArrowRight } from "lucide-react";

function CountdownPill({ date }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000 * 60);
    return () => clearInterval(i);
  }, []);
  const days = Math.max(0, Math.ceil((new Date(date).getTime() - now) / 86400000));
  return (
    <div className="inline-flex items-center gap-1 bg-nkf-gold text-nkf-navy px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest">
      T-{days} days
    </div>
  );
}

export default function UpcomingChampionships() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/championships", { params: { upcoming: true } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);
  const top = useMemo(() => items.slice(0, 4), [items]);
  return (
    <section data-testid="upcoming-championships" className="section-spacing bg-white">
      <div className="nkf-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-red font-semibold">Upcoming Championships</div>
            <h2 className="font-display text-3xl md:text-4xl text-nkf-blue mt-2">National & International Calendar</h2>
          </div>
          <Link to="/championships" className="text-sm font-semibold text-nkf-blue hover:text-nkf-red inline-flex items-center gap-1">
            View full calendar <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-6 sm:mt-8 items-stretch">
          {top.map((c) => (
            <article key={c.id} data-testid={`championship-${c.id}`} className="federation-card bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col h-full">
              <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                {c.poster_url ? (
                  <img src={c.poster_url} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400"><Trophy /></div>
                )}
                <div className="absolute top-3 left-3 bg-nkf-blue text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded">
                  {c.level}
                </div>
                <div className="absolute top-3 right-3">
                  <CountdownPill date={c.start_date} />
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-display text-lg text-nkf-blue leading-snug line-clamp-2">{c.title}</h3>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-nkf-red" /> {c.start_date} – {c.end_date}</div>
                  <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-nkf-red" /> {c.location}</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(c.categories || []).slice(0, 3).map((cat) => (
                    <span key={cat} className="text-[10.5px] uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{cat}</span>
                  ))}
                </div>
                <Link to={`/championships/${c.id}`} className="mt-5 inline-flex items-center justify-between text-sm font-semibold text-nkf-blue hover:text-nkf-red">
                  Registration & Details <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
          {!top.length && (
            <div className="col-span-full p-8 text-center text-slate-500 border border-dashed rounded-md">
              No upcoming championships at the moment.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
